import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { DATE_FORMATS } from 'lib/constants';

type DateRangeLabel = {
  label: string;
  range: string;
};

dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);

//get date, month (3-char), year separately from DD-MM-YYYY format
export function parseDateParts(dateStr: string) {
  const [dd, mm, yyyy] = dateStr.split('-');

  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  const monthIndex = Number(mm) - 1;

  return {
    day: dd,
    month: monthNames[monthIndex],
    year: yyyy,
  };
}

export const formatToDDMMYYYY = (date: string) => {
  return dayjs(date).format(DATE_FORMATS.DISPLAY);
};

//convert dd-mm-yyyy to yyyy-mm-dd
export const displayToAPIFormat = (date: string) => {
  const convertedDate = dayjs(date, DATE_FORMATS.DISPLAY).format(DATE_FORMATS.API);
  return convertedDate;
};

//convert date to string - mins ago, DAYS AGO ETC
export const formatLastLogin = (isoDate: string | Dayjs | null) => {
  if (!isoDate) return '--';

  const date = dayjs.isDayjs(isoDate) ? isoDate.toDate() : new Date(isoDate);
  const diffMs = Date.now() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} mins ago`;
  if (diffMins < 1440) return `${Math.floor(diffMins / 60)} hours ago`;

  return dayjs(isoDate).format(DATE_FORMATS.DISPLAY);
};
//convert yyyy-mm-dd to dd-mm-yyyy
export const apiToDisplayFormat = (date: string) => {
  const convertedDate = dayjs(date, DATE_FORMATS.API).format(DATE_FORMATS.DISPLAY);
  return convertedDate;
};

//converts to the mmm dd, yyyy format: ex Jan 30, 2024
export const formatToMonthShortDayYear = (date: Dayjs | string | Date) => {
  return dayjs(date).format(DATE_FORMATS.MMM_DD_YYYY);
};

export function getDateRangeLabel(
  from: Dayjs | string | Date,
  to: Dayjs | string | Date
): DateRangeLabel {
  if (!from || !to) {
    return { label: '', range: '' };
  }

  const start = dayjs(from).startOf('day');
  const end = dayjs(to).endOf('day');

  const today = dayjs().startOf('day');
  const yesterday = today.subtract(1, 'day');

  // Normalize diff (inclusive)
  const diffDays = end.diff(start, 'day') + 1;

  let label = '';

  // Detect presets (excluding today)
  if (end.isSame(yesterday, 'day')) {
    if (diffDays === 7) label = 'Last 7 days';
    else if (diffDays === 30) label = 'Last 30 days';
  }

  // This month
  const startOfThisMonth = today.startOf('month');
  if (start.isSame(startOfThisMonth, 'day') && end.isSame(today, 'day')) {
    label = 'This month';
  }

  // Last month
  const startOfLastMonth = today.subtract(1, 'month').startOf('month');
  const endOfLastMonth = today.subtract(1, 'month').endOf('month');
  if (start.isSame(startOfLastMonth, 'day') && end.isSame(endOfLastMonth, 'day')) {
    label = 'Last month';
  }

  // Format range
  const sameYear = start.year() === end.year();
  const sameMonth = start.month() === end.month() && sameYear;

  let range = '';

  if (sameYear && sameMonth) {
    // Apr 6 - Apr 12, 2026
    range = `${start.format('MMM D')} - ${end.format('MMM D, YYYY')}`;
  } else if (sameYear) {
    // Dec 12 - Jan 12, 2026
    range = `${start.format('MMM D')} - ${end.format('MMM D, YYYY')}`;
  } else {
    // Dec 12, 2025 to Jan 12, 2026
    range = `${start.format('MMM D, YYYY')} to ${end.format('MMM D, YYYY')}`;
  }

  return { label, range };
}

//Tue, 19th May 2026
export const formatDateWithOrdinal = (date = new Date()) => {
  const day = date.toLocaleDateString('en-US', { weekday: 'short' });
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  const year = date.getFullYear();

  const dayNumber = date.getDate();

  const getOrdinal = (n: number) => {
    if (n > 3 && n < 21) return 'th';

    switch (n % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  };

  return `${day}, ${dayNumber}${getOrdinal(dayNumber)} ${month} ${year}`;
};

export const isToday = (dateStr: string) => {
  return dayjs(dateStr).isSame(dayjs(), 'day');
};
